// HealthBanner — surfaces silent backend failures the moment the app boots.
//
// Why this exists: too many surfaces in the app fall back to `data || []`
// when a PostgREST query 401s/403s/errors, so pages render blank with no
// console signal. The "stock page empty" incident on 2026-05-05 was a JWT
// secret mismatch that took an hour to spot. This banner makes it five
// seconds.
//
// Rules:
//   - Only renders when there's an issue (no chrome on the happy path)
//   - Static position, no `position: fixed` (per dossier design standard)
//   - Self-dismissible per session, but reload always re-checks

import { useEffect, useState } from 'react';
import { runHealthCheck } from './healthCheck';

const KIND_LABELS = {
  error:     'API error',
  empty:     'no data',
  exception: 'request failed',
};

export default function HealthBanner() {
  const [issues, setIssues] = useState([]);
  const [checking, setChecking] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    runHealthCheck().then((found) => {
      if (cancelled) return;
      setIssues(found);
      setChecking(false);
      if (found.length > 0) {
        // Always log to console too — gives a copy-paste-able signal even
        // if the user dismisses the banner.
        // eslint-disable-next-line no-console
        console.warn('[health-check] failures:', found);
      }
    }).catch((e) => {
      if (cancelled) return;
      setIssues([{ table: '*', label: 'health probe', kind: 'exception', detail: e?.message || String(e) }]);
      setChecking(false);
    });
    return () => { cancelled = true; };
  }, []);

  if (checking || dismissed || issues.length === 0) return null;

  return (
    <div
      role="alert"
      style={{
        background: '#FEE4E2',
        color: '#7A2723',
        borderBottom: '1px solid #DC2626',
        padding: '8px 16px',
        fontSize: 13,
        fontFamily: 'system-ui, -apple-system, sans-serif',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flexWrap: 'wrap',
      }}
    >
      <strong style={{ fontWeight: 600 }}>
        System health: {issues.length} check{issues.length === 1 ? '' : 's'} failed
      </strong>
      <span style={{ opacity: 0.85 }}>
        {issues.map((i, idx) => (
          <span key={idx} style={{ marginRight: 12 }}>
            {i.label} ({KIND_LABELS[i.kind] || i.kind}{i.detail ? ` — ${i.detail}` : ''})
          </span>
        ))}
      </span>
      <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={btnStyle}
        >
          Retry
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          style={{ ...btnStyle, opacity: 0.7 }}
        >
          Dismiss
        </button>
      </span>
    </div>
  );
}

const btnStyle = {
  background: 'transparent',
  border: '1px solid #DC2626',
  color: '#7A2723',
  padding: '3px 10px',
  borderRadius: 4,
  fontSize: 12,
  fontFamily: 'inherit',
  cursor: 'pointer',
};
